import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Stack,
  Button,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
  Textarea,
  Divider,
  Alert,
  CircularProgress,
  Box,
  Card,
  Chip
} from '@mui/joy';
import { FaCalendar, FaLink, FaUser, FaMapMarkerAlt, FaClock, FaSearch } from 'react-icons/fa';
import { getGraphClient } from '../utils/graphService';
import { inclusiveEventEnd } from '../utils/outlookEventDates';
import { clearAllCaches } from '../hooks/useOutlookCalendar';
import { directSupabase } from '../lib/supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { IconWrapper } from './IconWrapper';
import { listProjectJobCards, updateJobCard } from '../pages/project-workspace/jobCard.service';
import type { JobCardRecord } from '../pages/project-workspace/jobCard.types';
import { allDayVersionOf, overrideFromOutlook } from '../pages/project-workspace/jobCardDiary';
import { withPerCardInstallOverride } from '../pages/project-workspace/jobCardInstallDate';

export interface OutlookEvent {
  id: string;
  subject: string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  location?: { displayName: string };
  attendees?: Array<{ emailAddress: { address: string; name: string }; type: string }>;
  bodyPreview?: string;
  isAllDay?: boolean;
  categories: string[];
}

interface Project {
  id: string;
  project_name: string;
  customer_name: string;
  customer_email?: string;
  customer_id?: number;
}

interface LinkCalendarEventModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Opens with this Outlook event already chosen (e.g. from the Link Outlook Events list). */
  initialEvent?: OutlookEvent | null;
}

const EVENT_TYPES = [
  'installationDateConfirmation',
  'firstSurveyAppointmentConfirmation',
  'finalSurveyAppointmentConfirmation',
  'deliveryDateConfirmation',
  'otherKeystoneServices'
] as const;

// Map template types to database enum values
const TEMPLATE_TO_ENUM_MAP: Record<string, string> = {
  'installationDateConfirmation': 'installationDateConfirmation',
  'firstSurveyAppointmentConfirmation': 'firstSurveyAppointmentConfirmation',
  'finalSurveyAppointmentConfirmation': 'finalSurveyAppointmentConfirmation',
  'deliveryDateConfirmation': 'deliveryDateConfirmation',
  'otherKeystoneServices': 'otherKeystoneServices'
};

type EventType = typeof EVENT_TYPES[number];

export const LinkCalendarEventModal: React.FC<LinkCalendarEventModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialEvent = null,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [selectedEvent, setSelectedEvent] = useState<OutlookEvent | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedEventType, setSelectedEventType] = useState<EventType>('otherKeystoneServices');
  const [customTitle, setCustomTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedCustomerAddress, setSelectedCustomerAddress] = useState<string>('');
  // Installation/delivery: which job card the event is for (job card 1 is the main booking)
  const [jobCards, setJobCards] = useState<JobCardRecord[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  /** Set when the chosen Outlook event is already a job card's diary entry. */
  const [ownerCard, setOwnerCard] = useState<{ title: string; projectName: string } | null>(null);
  
  // Data state
  const [outlookEvents, setOutlookEvents] = useState<OutlookEvent[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [suggestedEventType, setSuggestedEventType] = useState<EventType | null>(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDateRange, setSearchDateRange] = useState<'thisWeek' | 'lastWeek' | 'thisMonth' | 'future' | 'past' | 'all'>('thisWeek');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');

  // Load data when modal opens
  useEffect(() => {
    if (open) {
      loadOutlookEvents();
      loadProjects();
    }
  }, [open]);

  useEffect(() => {
    if (!selectedProjectId) {
      setSelectedProject(null);
      return;
    }
    const p = projects.find(p => p.id === selectedProjectId) || null;
    setSelectedProject(p);
  }, [selectedProjectId, projects]);

  const loadOutlookEvents = async (searchMode = false) => {
    try {
      setLoading(true);
      setIsSearching(searchMode);
      const client = await getGraphClient();
      
      // Get calendar ID
      const calendarsResponse = await client.api('/me/calendars').get();
      const calendar = calendarsResponse.value.find((cal: any) => cal.name === 'Calendar');
      if (!calendar) throw new Error('Calendar not found');
      
      // Determine date range based on search mode
      let startDate: Date | undefined;
      let endDate: Date | undefined;
      
      if (searchMode) {
        // Search mode - use custom date range if provided (support single-bound searches)
        if (customStartDate || customEndDate) {
          const today = new Date();
          if (customStartDate) {
            startDate = new Date(customStartDate);
          }
          if (customEndDate) {
            endDate = new Date(customEndDate);
            endDate.setHours(23, 59, 59);
          }
          // If only one bound provided, infer the other sensibly
          if (customStartDate && !customEndDate) {
            // Default to a 90 day window from start
            const inferredEnd = new Date(startDate!);
            inferredEnd.setDate(inferredEnd.getDate() + 90);
            endDate = inferredEnd;
          } else if (!customStartDate && customEndDate) {
            // Default to a 90 day window ending at endDate
            const inferredStart = new Date(endDate!);
            inferredStart.setDate(inferredStart.getDate() - 90);
            startDate = inferredStart;
          }
        } else {
          // Default search range based on selection
          const now = new Date();
          const startOfWeekMonday = (d: Date): Date => {
            const day = d.getDay(); // 0 Sun, 1 Mon, ...
            const deltaToMonday = (day + 6) % 7; // Mon->0, Sun->6
            const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - deltaToMonday);
            return monday;
          };
          switch (searchDateRange) {
            case 'thisWeek': {
              const mon = startOfWeekMonday(now);
              startDate = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate());
              endDate = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6, 23, 59, 59);
              break;
            }
            case 'lastWeek': {
              const mon = startOfWeekMonday(now);
              const lastMon = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() - 7);
              startDate = new Date(lastMon.getFullYear(), lastMon.getMonth(), lastMon.getDate());
              endDate = new Date(lastMon.getFullYear(), lastMon.getMonth(), lastMon.getDate() + 6, 23, 59, 59);
              break;
            }
            case 'thisMonth': {
              startDate = new Date(now.getFullYear(), now.getMonth(), 1);
              endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
              break;
            }
            case 'past':
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 365); // Last year
              endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1); // Yesterday
              break;
            case 'all':
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 365); // Last year
              endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 365); // Next year
              break;
            case 'future':
            default:
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Today
              endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 90); // Next 3 months
          }
        }
      } else {
        // Default mode - show today onwards
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Today
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 90); // Next 3 months
      }
      
      const params: any = {
        $select: 'id,subject,start,end,location,attendees,bodyPreview,isAllDay,categories',
        $orderby: 'start/dateTime asc'
      };
      // Build filter for the selected range
      const filterParts: string[] = [];
      if (startDate) filterParts.push(`start/dateTime ge '${startDate.toISOString()}'`);
      if (endDate) filterParts.push(`end/dateTime le '${endDate.toISOString()}'`);
      if (filterParts.length > 0) params.$filter = filterParts.join(' and ');

      const response = await client.api(`/me/calendars/${calendar.id}/events`).query(params).get();
      
      let events = response.value || [];
      
      // Filter by search query if provided
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        events = events.filter((event: OutlookEvent) => 
          event.subject?.toLowerCase().includes(query) ||
          event.bodyPreview?.toLowerCase().includes(query) ||
          event.location?.displayName?.toLowerCase().includes(query)
        );
      }
      
      // Sort and set after all processing
      setOutlookEvents(events);
    } catch (err: any) {
      setError('Failed to load Outlook events: ' + err.message);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const loadProjects = async () => {
    try {
      const { data, error } = await directSupabase
        .from('projects')
        .select('id,project_name,customer_name,customer_email,customer_id')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setProjects((data as any) || []);
    } catch (err: any) {
      setError('Failed to load projects: ' + err.message);
    }
  };

  const suggestEventType = (event: OutlookEvent): EventType | null => {
    const subject = event.subject?.toLowerCase() || '';
    const body = event.bodyPreview?.toLowerCase() || '';
    
    // Check for installation keywords
    if (subject.includes('install') || body.includes('install')) {
      return 'installationDateConfirmation';
    }
    
    // Check for final survey keywords first (more specific)
    if (subject.includes('final survey') || body.includes('final survey') || 
        subject.includes('final survey') || body.includes('final survey')) {
      return 'finalSurveyAppointmentConfirmation';
    }
    
    // Check for first survey keywords
    if (subject.includes('first survey') || body.includes('first survey') ||
        subject.includes('survey') || body.includes('survey')) {
      return 'firstSurveyAppointmentConfirmation';
    }
    
    // Check for delivery keywords
    if (subject.includes('delivery') || body.includes('delivery')) {
      return 'deliveryDateConfirmation';
    }
    
    // Check duration (installations are usually longer)
    const start = new Date(event.start.dateTime);
    const end = new Date(event.end.dateTime);
    const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    
    if (durationHours > 4) {
      return 'installationDateConfirmation';
    }
    
    return null;
  };

  // Function to generate event title based on convention
  const generateEventTitle = (event: OutlookEvent, project: Project, eventType: EventType, customerAddress?: string): string => {
    // Map event types to shorter display names
    const eventTypeMap: Record<EventType, string> = {
      'installationDateConfirmation': 'Installation',
      'firstSurveyAppointmentConfirmation': 'First Survey',
      'finalSurveyAppointmentConfirmation': 'Final Survey',
      'deliveryDateConfirmation': 'Delivery',
      'otherKeystoneServices': 'Other Services'
    };
    
    const eventTypeDisplay = eventTypeMap[eventType] || eventType;
    // Prefer CRM address, else project address fields, else fallback
    const projectAddress = ((project as any)?.address || (project as any)?.project_address || '').toString();
    const address = (customerAddress || projectAddress || '').trim();
    const fallback = (project.project_name || event.subject || '').trim();
    const detail = address || fallback;
    return `${eventTypeDisplay} - ${project.customer_name}${detail ? ` - ${detail}` : ''}`;
  };

  // Helper to fetch customer address from customers table by email
  const fetchCustomerAddress = async (email?: string | null, customerId?: string | number | null, customerName?: string | null): Promise<string> => {
    try {
      // Use a single RPC-safe filter to avoid 406 content negotiation issues
      if (email) {
        // Try exact, then case-insensitive
        let { data, error } = await directSupabase
          .from('customers')
          .select('address')
          .eq('email', email)
          .limit(1);
        if (!error && data && data[0]?.address) return (data[0] as any).address as string;
        const { data: data2 } = await directSupabase
          .from('customers')
          .select('address')
          .ilike('email', email)
          .limit(1);
        if (data2 && data2[0]?.address) return (data2[0] as any).address as string;
      }
      if (customerId !== undefined && customerId !== null) {
        const { data } = await directSupabase
          .from('customers')
          .select('address')
          .eq('organization_id', customerId)
          .limit(1);
        if (data && data[0]?.address) return (data[0] as any).address as string;
      }
      if (customerName) {
        // Try both organization_name and name fields
        let { data } = await directSupabase
          .from('customers')
          .select('address')
          .ilike('organization_name', `%${customerName}%`)
          .limit(1);
        if (data && data[0]?.address) return (data[0] as any).address as string;
        const { data: dataAlt } = await directSupabase
          .from('customers')
          .select('address')
          .ilike('name', `%${customerName}%`)
          .limit(1);
        if (dataAlt && dataAlt[0]?.address) return (dataAlt[0] as any).address as string;
      }
      return '';
    } catch {
      return '';
    }
  };

  const handleEventSelect = (event: OutlookEvent) => {
    console.log('Event selected:', event);
    setSelectedEvent(event);
    
    // Suggest event type
    const suggestion = suggestEventType(event);
    console.log('Suggested event type:', suggestion);
    setSuggestedEventType(suggestion);
    if (suggestion) {
      setSelectedEventType(suggestion);
    }
    
    // Auto-populate title if project is selected
    if (selectedProject) {
      console.log('Project already selected:', selectedProject);
      const autoTitle = generateEventTitle(event, selectedProject, suggestion || EVENT_TYPES[0], selectedCustomerAddress);
      console.log('Auto-generated title:', autoTitle);
      setCustomTitle(autoTitle);
    } else {
      console.log('No project selected, using event subject');
      // Fallback to event subject if no project selected
      setCustomTitle(event.subject || '');
    }
  };

  useEffect(() => {
    if (open && initialEvent) handleEventSelect(initialEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialEvent?.id]);

  useEffect(() => {
    let cancelled = false;
    setJobCards([]);
    setSelectedCardId('');
    if (!selectedProject?.id) return;
    listProjectJobCards(String(selectedProject.id))
      .then((cards) => {
        if (cancelled) return;
        setJobCards(cards);
        setSelectedCardId(cards[0]?.id || '');
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [selectedProject?.id]);

  useEffect(() => {
    let cancelled = false;
    setOwnerCard(null);
    if (!selectedEvent?.id) return;
    (async () => {
      const { data } = await (directSupabase as any)
        .from('project_job_cards')
        .select('title, project:projects(project_name)')
        .eq('calendar_event_id', selectedEvent.id)
        .limit(1);
      const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (!cancelled && row) {
        setOwnerCard({ title: row.title || 'Job card', projectName: row.project?.project_name || 'a workspace' });
      }
    })().catch(() => undefined);
    return () => { cancelled = true; };
  }, [selectedEvent?.id]);

  const isBookingType = selectedEventType === 'installationDateConfirmation' || selectedEventType === 'deliveryDateConfirmation';
  const laterCard = isBookingType && jobCards.length > 1
    ? jobCards.slice(1).find((card) => card.id === selectedCardId) || null
    : null;

  /** A later job card takes the Outlook event as its own dates; the main booking is untouched. */
  const linkToLaterCard = async (card: JobCardRecord, event: OutlookEvent, projectId: string) => {
    const read = overrideFromOutlook(event);
    if (!read) throw new Error('Could not read the dates of this Outlook event.');
    // A multi-day timed event becomes all day when linked, so the card keeps its start time but no finish
    const override = allDayVersionOf(event, '') ? { ...read, end: read.endDay } : read;
    await updateJobCard(card.id, {
      install_date_override: override.start.slice(0, 10),
      install_end_date_override: override.endDay,
      calendar_event_id: event.id,
    });
    const { data: projectRow, error: projectErr } = await directSupabase
      .from('projects')
      .select('metadata')
      .eq('id', projectId)
      .single();
    if (projectErr) throw projectErr;
    const { error: metaErr } = await directSupabase
      .from('projects')
      .update({ metadata: withPerCardInstallOverride(projectRow || {}, card.id, override.start, override.end) })
      .eq('id', projectId);
    if (metaErr) throw metaErr;
  };

  const handleLink = async () => {
    if (!selectedEvent || !selectedProject || !user) {
      setError('Please select both an event and a project');
      return;
    }

    if (ownerCard) {
      setError(`This Outlook event is ${ownerCard.title}'s diary entry for ${ownerCard.projectName}. Change it from that job card instead.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const eventType = TEMPLATE_TO_ENUM_MAP[selectedEventType] || 'other';

      if (laterCard) {
        await linkToLaterCard(laterCard, selectedEvent, String(selectedProject.id));
        try {
          const client = await getGraphClient();
          const subject = customTitle || selectedEvent.subject;
          const patchBody: any = allDayVersionOf(selectedEvent, subject) || { subject };
          if (selectedCustomerAddress) patchBody.location = { displayName: selectedCustomerAddress };
          await client.api(`/me/events/${selectedEvent.id}`).patch(patchBody);
        } catch {}
        clearAllCaches();
        onSuccess();
        onClose();
        return;
      }

      // One live booking per type: reuse the best existing one (already this Outlook event,
      // else a live one, else the latest) and cancel the rest, so no stale duplicate keeps old dates
      const { data: existingRows } = await directSupabase
        .from('project_events')
        .select('id, event_date, end_date, calendar_event_id, title, status, updated_at')
        .eq('project_id', selectedProject.id)
        .eq('event_type', eventType);
      const existing = [...((existingRows || []) as any[])].sort((a, b) => {
        const rank = (row: any) => (row.calendar_event_id === selectedEvent.id ? 2 : row.status !== 'cancelled' ? 1 : 0);
        return rank(b) - rank(a) || (Date.parse(b.updated_at || '') || 0) - (Date.parse(a.updated_at || '') || 0);
      });

      const oldValues = existing.length > 0 ? existing[0] : null;
      const baseUpdate = {
        title: customTitle || selectedEvent.subject,
        event_date: selectedEvent.start.dateTime,
        end_date: inclusiveEventEnd(selectedEvent),
        calendar_event_id: selectedEvent.id,
        notes: notes,
        // Linking makes the booking live again, even if it had been cancelled
        status: 'scheduled',
        updated_at: new Date().toISOString(),
        updated_by: user.id
      } as any;

      let projectEventId: string;
      if (oldValues) {
        // Update existing to avoid duplicates
        const { data: updated, error: updErr } = await directSupabase
          .from('project_events')
          .update(baseUpdate)
          .eq('id', (oldValues as any).id)
          .select('id')
          .single();
        if (updErr) throw updErr;
        projectEventId = (updated as any).id;
        // Record audit history
        await directSupabase.from('project_event_history').insert({
          event_id: projectEventId,
          change_type: 'UPDATE',
          changed_at: new Date().toISOString(),
          changed_by: user.id,
          old_values: oldValues,
          new_values: baseUpdate
        });
        const duplicates = existing.slice(1).filter((row: any) => row.status !== 'cancelled');
        if (duplicates.length > 0) {
          const cancelled = { status: 'cancelled', updated_at: new Date().toISOString(), updated_by: user.id };
          const { error: cancelErr } = await directSupabase
            .from('project_events')
            .update(cancelled)
            .in('id', duplicates.map((row: any) => row.id));
          if (cancelErr) throw cancelErr;
          await directSupabase.from('project_event_history').insert(duplicates.map((row: any) => ({
            event_id: row.id,
            change_type: 'UPDATE',
            changed_at: cancelled.updated_at,
            changed_by: user.id,
            old_values: row,
            new_values: cancelled
          })));
        }
      } else {
        // Insert new
        const insertData = {
          project_id: selectedProject.id,
          event_type: eventType,
          status: 'scheduled',
          is_automated: false,
          created_by: user.id,
          updated_by: user.id,
          ...baseUpdate
        };
        const { data: inserted, error: insErr } = await directSupabase
          .from('project_events')
          .insert(insertData)
          .select('id')
          .single();
        if (insErr) throw insErr;
        projectEventId = (inserted as any).id;
        await directSupabase.from('project_event_history').insert({
          event_id: projectEventId,
          change_type: 'INSERT',
          changed_at: new Date().toISOString(),
          changed_by: user.id,
          old_values: null,
          new_values: insertData
        });
      }

      // Update Outlook event subject/location so Work Calendar reflects custom title and address.
      // A multi-day timed install or delivery becomes all day; the booking keeps its start time.
      try {
        const client = await getGraphClient();
        const subject = customTitle || selectedEvent.subject;
        const patchBody: any = (isBookingType && allDayVersionOf(selectedEvent, subject)) || { subject };
        if (selectedCustomerAddress) {
          patchBody.location = { displayName: selectedCustomerAddress };
        }
        await client.api(`/me/events/${selectedEvent.id}`).patch(patchBody);
      } catch {}

      // Clear caches to reflect updates immediately
      clearAllCaches();

      // Success
      onSuccess();
      onClose();
    } catch (err: any) {
      setError('Failed to link event: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-GB', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter projects for Step 2 based on search
  const filteredProjects = projects.filter((project) => {
    const q = projectSearchQuery.trim().toLowerCase();
    if (!q) return true;
    const name = project.project_name?.toLowerCase() || '';
    const customer = project.customer_name?.toLowerCase() || '';
    const email = project.customer_email?.toLowerCase() || '';
    return (
      name.includes(q) ||
      customer.includes(q) ||
      email.includes(q)
    );
  });

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" maxWidth="md" sx={{ maxHeight: '90vh', overflow: 'hidden' }}>
        <ModalClose />
        <Typography level="h4" component="h2" mb={2}>
          <IconWrapper icon={FaLink} style={{ marginRight: '8px' }} />
          Link Calendar Event to Project
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ overflow: 'auto', maxHeight: 'calc(90vh - 200px)' }}>
          <Stack spacing={3}>
          {/* Step 1: Select Outlook Event */}
          <Box>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Step 1: Select Calendar Event
            </Typography>
            
            {/* Search Controls */}
            <Stack spacing={2} sx={{ mb: 2 }}>
              <FormControl>
                <FormLabel>Search Events</FormLabel>
                <Input
                  placeholder="Search by subject, description, or location..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  startDecorator={<IconWrapper icon={FaSearch} />}
                />
              </FormControl>
              
              <Stack direction="row" spacing={2}>
                <FormControl>
                  <FormLabel>Date Range</FormLabel>
                  <Select
                    value={searchDateRange}
                    onChange={(_, value) => setSearchDateRange(value as any)}
                  >
                    <Option value="thisWeek">This Week</Option>
                    <Option value="lastWeek">Last Week</Option>
                    <Option value="thisMonth">This Month</Option>
                    <Option value="future">Future Events</Option>
                    <Option value="past">Past Events</Option>
                    <Option value="all">All Events</Option>
                  </Select>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Custom Start Date</FormLabel>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomStartDate(e.target.value)}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Custom End Date</FormLabel>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomEndDate(e.target.value)}
                  />
                </FormControl>
              </Stack>
              
              <Button
                onClick={() => loadOutlookEvents(true)}
                loading={isSearching}
              >
                Search Events
              </Button>
            </Stack>
            
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 'sm', p: 1 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1 }}>
                    Showing {outlookEvents.length} events
                    {searchQuery.trim() && ` matching "${searchQuery}"`}
                    {searchDateRange !== 'future' && ` (${searchDateRange} events)`}
                  </Typography>
                <Stack spacing={1}>
                  {outlookEvents.map((event) => (
                    <Card
                      key={event.id}
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'background.level1' },
                        ...(selectedEvent?.id === event.id && {
                          borderColor: 'primary.500',
                          bgcolor: 'primary.50'
                        })
                      }}
                      onClick={() => handleEventSelect(event)}
                    >
                      <Box p={1}>
                        <Typography level="title-sm" mb={0.5}>
                          {event.subject}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconWrapper icon={FaClock} style={{ fontSize: '12px' }} />
                          <Typography level="body-xs">
                            {formatDateTime(event.start.dateTime)}
                          </Typography>
                          {event.location?.displayName && (
                            <>
                              <IconWrapper icon={FaMapMarkerAlt} style={{ fontSize: '12px' }} />
                              <Typography level="body-xs">
                                {event.location.displayName}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </Box>
                    </Card>
                  ))}
                </Stack>
                </>
              )}
            </Box>
          </Box>

          {/* Step 2: Select Project */}
          <Box>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Step 2: Select Project
            </Typography>
            <Stack spacing={1.5}>
              <FormControl>
                <FormLabel>Search Projects</FormLabel>
                <Input
                  placeholder="Search by project, customer, or email..."
                  value={projectSearchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectSearchQuery(e.target.value)}
                  startDecorator={<IconWrapper icon={FaSearch} />}
                />
              </FormControl>
              <FormControl>
                <Select
                  value={selectedProjectId}
                  onChange={async (_, value) => {
                    const id = (value as string) || '';
                    setSelectedProjectId(id);
                    const project = projects.find(p => p.id === id) || null;
                    console.log('Project selected:', project);
                    setSelectedProject(project);
                    // Fetch address from customers table
                    let addr = await fetchCustomerAddress(project?.customer_email, (project as any)?.customer_id, project?.customer_name);
                    if (!addr) {
                      // Address not found - will use empty string
                      addr = '';
                    }
                    console.debug('Resolved customer address:', addr);
                    setSelectedCustomerAddress(addr);
                    // Auto-populate title when project is selected
                    if (project && selectedEvent) {
                      console.log('Both project and event selected, generating title');
                      const autoTitle = generateEventTitle(selectedEvent, project, selectedEventType, addr);
                      console.log('Auto-generated title:', autoTitle);
                      setCustomTitle(autoTitle);
                    }
                  }}
                  placeholder="Choose a project..."
                >
                  {filteredProjects.map((project) => (
                    <Option key={project.id} value={project.id}>
                      <Box>
                        <Typography level="title-sm">{project.project_name}</Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {project.customer_name}
                        </Typography>
                      </Box>
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {ownerCard && (
            <Alert color="warning" sx={{ mb: 2 }}>
              This Outlook event is already {ownerCard.title}&apos;s diary entry for {ownerCard.projectName}. Change its dates from that job card, or with Edit Event in Work Calendar.
            </Alert>
          )}

          {/* Step 3: Configure Event */}
          <Box>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Step 3: Configure Event
            </Typography>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Event Type</FormLabel>
                <Select
                  value={selectedEventType}
                  onChange={(_, value) => {
                    const newEventType = value as EventType;
                    setSelectedEventType(newEventType);
                    
                    // Update title when event type changes
                    if (selectedEvent && selectedProject) {
                      const autoTitle = generateEventTitle(selectedEvent, selectedProject, newEventType);
                      setCustomTitle(autoTitle);
                    }
                  }}
                >
                  {EVENT_TYPES.map((type) => (
                    <Option key={type} value={type}>
                      {type} {type === suggestedEventType && '(Suggested)'}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              {isBookingType && jobCards.length > 1 && (
                <FormControl>
                  <FormLabel>Job card</FormLabel>
                  <Select value={selectedCardId || null} onChange={(_, value) => setSelectedCardId(String(value || ''))}>
                    {jobCards.map((card, index) => (
                      <Option key={card.id} value={card.id}>
                        {card.title}{index === 0 ? ' (main booking)' : ''}
                      </Option>
                    ))}
                  </Select>
                  <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
                    {laterCard
                      ? `This event becomes ${laterCard.title}'s dates. The main booking is not changed.`
                      : 'This event becomes the main booking for the job.'}
                  </Typography>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Title</FormLabel>
                <Input
                  value={customTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomTitle(e.target.value)}
                  placeholder="Event title..."
                />
              </FormControl>

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  minRows={2}
                />
              </FormControl>
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleLink}
              disabled={!selectedEvent || !selectedProjectId || !selectedProject || loading || !!ownerCard}
              loading={loading}
            >
              Link Event
            </Button>
          </Stack>
        </Stack>
        </Box>
      </ModalDialog>
    </Modal>
  );
}; 